/**
 * 静态模版/头像：
 * - 0 = 1:1
 * - 2 = 9:16 全屏（方图画布，成图裁左右）
 * - 3 = 3:4（768×1024 画布，不留白，整张缩成成图）
 * （1 仅用于 Spine/动态全屏，静态侧不用）
 */
export type FullscreenType = 0 | 1 | 2 | 3

export function normalizeFullscreenType(
  value?: number | null,
): FullscreenType {
  if (value === 1) return 1
  if (value === 2) return 2
  if (value === 3) return 3
  return 0
}

/** 静态侧只认 0 / 2 / 3，其余按 1:1 */
export function normalizeStaticFullscreenType(
  value?: number | null,
): 0 | 2 | 3 {
  const type = normalizeFullscreenType(value)
  return type === 1 ? 0 : type
}

/** 静态侧是否 9:16 全屏画幅 */
export function isFullscreenAspect(
  fullscreenType?: number | null,
): boolean {
  return normalizeFullscreenType(fullscreenType) === 2
}

/** 静态侧是否 3:4 画幅 */
export function isPortraitAspect(fullscreenType?: number | null): boolean {
  return normalizeFullscreenType(fullscreenType) === 3
}

export function getFullscreenTypeLabel(
  fullscreenType?: number | null,
): string {
  if (isFullscreenAspect(fullscreenType)) return '9:16'
  if (isPortraitAspect(fullscreenType)) return '3:4'
  return '1:1'
}

/** 列表 / 卡片用的 tailwind 比例类 */
export function getAspectClass(fullscreenType?: number | null): string {
  if (isFullscreenAspect(fullscreenType)) return 'aspect-[9/16]'
  if (isPortraitAspect(fullscreenType)) return 'aspect-[3/4]'
  return 'aspect-square'
}

/** 组件逻辑画布：普通 512²，全屏 1024²，3:4 为 768×1024 */
export function getComponentCanvasSize(
  fullscreenType?: number | null,
): { w: number; h: number } {
  if (isPortraitAspect(fullscreenType)) return { w: 768, h: 1024 }
  if (isFullscreenAspect(fullscreenType)) return { w: 1024, h: 1024 }
  return { w: 512, h: 512 }
}

/** 组件逻辑坐标 / 合成画布宽度：全屏 1024，3:4 768，普通 512 */
export function getComponentBaseSize(
  fullscreenType?: number | null,
): number {
  return getComponentCanvasSize(fullscreenType).w
}

/** Avatar / Frame 输出尺寸 */
export function getAvatarOutputDimensions(
  fullscreenType?: number | null,
): { w: number; h: number } {
  if (isFullscreenAspect(fullscreenType)) {
    return { w: 512, h: 912 }
  }
  if (isPortraitAspect(fullscreenType)) {
    return { w: 512, h: 683 }
  }
  return { w: 512, h: 512 }
}

/** 组件封面尺寸：手动上传一律 512 方图（与是否全屏无关） */
export function getComponentCoverDimensions(
  _fullscreenType?: number | null,
): { w: number; h: number } {
  return { w: 512, h: 512 }
}

/** 从正方形画布居中裁成目标尺寸（与 Spine fullscreen 一致） */
export function cropCenterFromSquare(
  squareCanvas: HTMLCanvasElement,
  outputWidth: number,
  outputHeight: number,
): HTMLCanvasElement {
  const size = squareCanvas.width
  const x = Math.round((size - outputWidth) / 2)
  const y = Math.round((size - outputHeight) / 2)
  const out = document.createElement('canvas')
  out.width = outputWidth
  out.height = outputHeight
  const ctx = out.getContext('2d')!
  ctx.drawImage(
    squareCanvas,
    x,
    y,
    outputWidth,
    outputHeight,
    0,
    0,
    outputWidth,
    outputHeight,
  )
  return out
}
